import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Send } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isStandalone } from "../lib/pwa";

export const TOPICS = ["Facebook-forbindelse", "SMS", "Faktura og abonnement", "Medarbejdere/udlevering", "Fejl i appen", "Andet"];
const MAIL = "info@jatakpro.dk";

const appVersion = () => {
  const src = [...document.scripts].map((s) => s.src).find((s) => /main\.[\w]+\.js/.test(s));
  return `web ${src ? src.match(/main\.([\w]+)\.js/)[1].slice(0, 8) : "dev"}${isStandalone() ? " (app)" : ""}`;
};

function Success() {
  return (
    <div className="sup-form sup-done" role="status" data-testid="support-success">
      <CheckCircle2 size={40} />
      <h2>Tak! Vi vender tilbage hurtigst muligt – typisk inden for 1 hverdag.</h2>
      <p className="muted">Du får en kvittering på mail om lidt.</p>
    </div>
  );
}

export default function SupportForm() {
  const { store } = useAuth();
  const owner = store && !store.staff && !store.is_owner ? store : null;
  const [f, setF] = useState({ name: "", email: "", store_name: "", topic: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (owner) setF((x) => ({ ...x, email: x.email || owner.email, store_name: x.store_name || owner.name }));
  }, [owner?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const miss = [
      f.name.trim().length < 2 && "Skriv dit navn.",
      !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(f.email.trim()) && "Skriv en gyldig e-mailadresse.",
      !f.topic && "Vælg et emne.",
      f.message.trim().length < 10 && "Beskeden skal være mindst 10 tegn.",
    ].filter(Boolean);
    if (miss.length) return setErr(miss.join(" "));
    setBusy(true); setErr("");
    try {
      await api.post("/support/contact", { ...f, app_version: owner ? appVersion() : "" });
      setDone(true);
    } catch (ex) {
      setErr(ex.response ? apiError(ex.response.data?.detail) : "Beskeden kunne ikke sendes – tjek din forbindelse.");
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success />;
  return (
    <form className="sup-form" onSubmit={submit} noValidate data-testid="support-form">
      <h2>Skriv til os</h2>
      <div className="sup-row">
        <div className="field">
          <label htmlFor="sup-name">Navn</label>
          <input id="sup-name" value={f.name} onChange={set("name")} required minLength={2} maxLength={100} autoComplete="name" data-testid="support-name" />
        </div>
        <div className="field">
          <label htmlFor="sup-email">E-mail</label>
          <input id="sup-email" type="email" value={f.email} onChange={set("email")} required maxLength={200} autoComplete="email" data-testid="support-email" />
        </div>
      </div>
      <div className="sup-row">
        <div className="field">
          <label htmlFor="sup-store">Butiksnavn <span className="muted">(valgfrit)</span></label>
          <input id="sup-store" value={f.store_name} onChange={set("store_name")} maxLength={120} autoComplete="organization" data-testid="support-store" />
        </div>
        <div className="field">
          <label htmlFor="sup-topic">Emne</label>
          <select id="sup-topic" value={f.topic} onChange={set("topic")} required data-testid="support-topic">
            <option value="" disabled>Vælg emne</option>
            {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="sup-msg">Besked</label>
        <textarea id="sup-msg" rows={6} value={f.message} onChange={set("message")} required minLength={10} maxLength={5000}
          placeholder="Beskriv kort, hvad det drejer sig om – fx hvilket tilbud eller hvilken skærm." data-testid="support-message" />
        <div className="hint">{f.message.length}/5000</div>
      </div>
      <div className="sup-hp" aria-hidden="true">
        <label htmlFor="sup-web">Hjemmeside</label>
        <input id="sup-web" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} data-testid="support-hp" />
      </div>
      {err && (
        <div className="notice error" role="alert" data-testid="support-error">
          {err} Du kan også skrive direkte til <a href={`mailto:${MAIL}`}>{MAIL}</a>.
        </div>
      )}
      <p className="sup-consent" data-testid="support-consent">
        Vi bruger dine oplysninger til at besvare din henvendelse. Læs mere i vores <Link to="/privatliv">privatlivspolitik</Link>.
      </p>
      <button className="btn primary big" disabled={busy} data-testid="support-submit">
        <Send size={16} /> {busy ? "Sender…" : "Send besked"}
      </button>
    </form>
  );
}
